import { useState, useReducer, useCallback } from "react";
import { useAuth } from "../context/authContext";
import { useNavigate } from "react-router";
import { API_URL, API_METHODS } from "../../constants";

const dataFetchReducer = (state, action) => {
  switch (action.type) {
    case "FETCH_INIT":
      return { ...state, isLoading: true, isError: false, error: null };

    case "FETCH_SUCCESS":
      return {
        ...state,
        data: action.payload.result,
        status: action.payload.status,
        isLoading: false,
        isError: false,
        error: null,
      };

    case "FETCH_FAILURE":
      return {
        ...state,
        data: action.payload.result,
        error: action.payload.error,
        status: action.payload.status,
        isLoading: false,
        isError: true,
      };

    default:
      return state;
  }
};

const getDataApiOptions = (method, requestBody, token) => {
  let options = {
    method: method,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(token && { "X-Session-ID": token }),
      "X-Locale": localStorage.getItem("selectedLanguage"),
    },
  };
  if (requestBody) {
    options.body = JSON.stringify(requestBody);
  }
  return options;
};

const extractErrorObject = (result) => {
  try {
    if (
      result?.errors &&
      Array.isArray(result.errors) &&
      result.errors.length > 0
    ) {
      const firstError = result.errors[0];
      return {
        displayMessage: firstError.displayMessage,
        errorCode: firstError.errorCode,
      };
    }

    if (result?.error) {
      if (typeof result.error === "object" && result.error.message) {
        return result.error;
      }
      return { message: result.error };
    }

    return {
      message: typeof result === "string" ? result : "An error occurred",
    };
  } catch (error) {
    // Fallback if extraction fails
    console.error("Error extracting error object:", error);
    return {
      message: "An unexpected error occurred",
      errorCode: "EXTRACTION_ERROR",
    };
  }
};

const useApi = (apiPath, body, method) => {
  const { token, setToken, clearToken } = useAuth();
  const navigate = useNavigate();
  let options = getDataApiOptions(method, body, token);
  const [url] = useState(API_URL + apiPath);
  const [state, dispatch] = useReducer(dataFetchReducer, {
    isLoading: null,
    data: null,
    request: body,
  });

  const callApi = useCallback(
    async (body, newMethod, newAPIPath) => {
      let APIUrl = url;
      if (newAPIPath) {
        APIUrl = API_URL + newAPIPath;
      }

      if (newMethod) {
        method = newMethod;
      }

      let didCancel = false;

      options = getDataApiOptions(method, body, token);

      if (body) {
        options.body = JSON.stringify(body);
      }
      if (method === API_METHODS.GET) {
        options.body = null;
      }

      options.method = method;
      dispatch({ type: "FETCH_INIT" });

      const fetchRequest = new Request(APIUrl, options);

      try {
        let response = await fetch(fetchRequest);
        let status = response.status;
        let result = null;

        if (status !== 204) {
          result = await response.json();
        }

        const sessionId = response.headers.get("X-Session-Id");
        if (sessionId) {
          setToken(sessionId);
        }

        if (!didCancel) {
          // Handle 204 No Content
          if (status === 204) {
            dispatch({
              type: "FETCH_SUCCESS",
              payload: { result: null, status },
            });
            return;
          }

          // Handle success responses FIRST (2xx)
          if (status >= 200 && status < 300) {
            dispatch({
              type: "FETCH_SUCCESS",
              payload: { result, status },
            });
            return;
          }

          // Only extract errors for non-success responses
          let errorObject;
          try {
            errorObject = extractErrorObject(result);
          } catch (extractionError) {
            console.error("Failed to extract error object:", extractionError);
            errorObject = {
              message: "An unexpected error occurred",
              errorCode: "EXTRACTION_ERROR",
            };
          }

          // Handle specific error codes requiring navigation
          if (
            errorObject.errorCode === "PROCESSING_ERROR" ||
            errorObject.errorCode === "INTERNAL_SERVER_ERROR" ||
            errorObject.errorCode === "SESSION_TIMEDOUT"
          ) {
            const errorTypeMap = {
              PROCESSING_ERROR: "processing",
              INTERNAL_SERVER_ERROR: "internal",
              SESSION_TIMEDOUT: "session",
            };

            navigate("/error", {
              replace: true,
              state: {
                type: errorTypeMap[errorObject.errorCode],
                status: status,
                message: errorObject.displayMessage,
              },
            });
            return;
          }

          // Handle 401 Unauthorized
          if (status === 401) {
            clearToken();
            dispatch({
              type: "FETCH_FAILURE",
              payload: {
                result,
                status,
                error: errorObject,
              },
            });
            return;
          }

          // Handle other 4xx client errors
          if (status >= 400 && status < 500) {
            dispatch({
              type: "FETCH_FAILURE",
              payload: {
                result,
                status,
                error: errorObject,
              },
            });
            return;
          }

          // Handle 5xx server errors
          if (status >= 500 && status < 600) {
            navigate("/error", {
              replace: true,
              state: {
                type: "server",
                status: status,
                message: errorObject.displayMessage || "Server error occurred",
              },
            });
            return;
          }

          // Fallback for any other unexpected status codes
          dispatch({
            type: "FETCH_FAILURE",
            payload: {
              result,
              status,
              error: errorObject,
            },
          });
        }
      } catch (error) {
        if (!didCancel) {
          // Navigate to error page for network errors
          navigate("/error", {
            replace: true,
            state: {
              type: "network",
              originalError: error.message,
            },
          });
        }
      }
    },
    [url, method, token, setToken, clearToken, navigate]
  );

  return [state, callApi];
};

export default useApi;
